import * as types from "./types";
import OverleafAPI from "../../services/overleaf";

import { normalize, schema } from "../normalize";

const cardSchema = new schema.Entity("cards", {}, { idAttribute: "uuid" });

const defaultCardEntities = { cards: {} };

const deckSchema = new schema.Entity(
  "decks",
  {
    cards: [cardSchema]
  },
  { idAttribute: "uuid" }
);

const deckListSchema = new schema.Array(deckSchema);

const defaultDeckEntities = {
  decks: {},
  cards: {}
};

const fetchDecksRequest = () => ({
  type: types.FETCH_DECKS_REQUEST
});

const fetchDecksSuccess = data => ({
  type: types.FETCH_DECKS_SUCCESS,
  data
});

const fetchDecksFailure = error => ({
  type: types.FETCH_DECKS_FAILURE,
  error
});

export const fetchDecks = meta => async dispatch => {
  // TODO should we be storing what meta was used? Should the state be broken up by isbn?
  dispatch(fetchDecksRequest());
  await OverleafAPI.init();
  return OverleafAPI.Decks.all(meta)
    .then(({ decks }) => {
      dispatch(
        fetchDecksSuccess(normalize(decks, deckListSchema, defaultDeckEntities))
      );
    })
    .catch(error => dispatch(fetchDecksFailure(error)));
};

const fetchDeckRequest = deckId => ({
  type: types.FETCH_DECK_REQUEST,
  deckId
});

const fetchDeckSuccess = (deckId, data) => ({
  type: types.FETCH_DECK_SUCCESS,
  deckId,
  data
});

const fetchDeckFailure = (deckId, error) => ({
  type: types.FETCH_DECK_FAILURE,
  deckId,
  error
});

export const fetchDeck = deckId => async dispatch => {
  dispatch(fetchDeckRequest(deckId));
  await OverleafAPI.init();
  return OverleafAPI.Decks.one(deckId)
    .then(({ deck }) =>
      dispatch(
        fetchDeckSuccess(
          deckId,
          normalize(deck, deckSchema, defaultDeckEntities)
        )
      )
    )
    .catch(error => dispatch(fetchDeckFailure(deckId, error)));
};

const updateDeckRequest = (deckId, data) => ({
  type: types.UPDATE_DECK_REQUEST,
  deckId,
  data
});

const updateDeckSuccess = (deckId, data) => ({
  type: types.UPDATE_DECK_SUCCESS,
  deckId,
  data
});

const updateDeckFailure = (deckId, error, oldData) => ({
  type: types.UPDATE_DECK_FAILURE,
  deckId,
  data: oldData, // TODO is this normalized?
  error
});

export const updateDeck = (deck, data) => async dispatch => {
  const deckId = deck.uuid;
  dispatch(updateDeckRequest(deckId, data));
  await OverleafAPI.init();
  return OverleafAPI.Decks.update(deckId, data)
    .then(({ deck }) => {
      dispatch(
        updateDeckSuccess(
          deckId,
          normalize(deck, deckSchema, defaultDeckEntities)
        )
      );
    })
    .catch(error => dispatch(updateDeckFailure(deck.uuid, error, deck)));
};

const deleteDeckRequest = deckId => ({
  type: types.DELETE_DECK_REQUEST,
  deckId
});

const deleteDeckSuccess = deckId => ({
  type: types.DELETE_DECK_SUCCESS,
  deckId
});

const deleteDeckFailure = (deckId, error) => ({
  type: types.DELETE_DECK_FAILURE,
  deckId,
  error
});

export const deleteDeck = deckId => async dispatch => {
  dispatch(deleteDeckRequest(deckId));
  await OverleafAPI.init();
  return OverleafAPI.Decks.delete(deckId)
    .then(() => dispatch(deleteDeckSuccess(deckId)))
    .catch(error => dispatch(deleteDeckFailure(deckId, error)));
};

const createDeckRequest = () => ({
  type: types.CREATE_DECK_REQUEST
});

const createDeckSuccess = data => ({
  type: types.CREATE_DECK_SUCCESS,
  data
});

const createDeckFailure = error => ({
  type: types.CREATE_DECK_FAILURE,
  error
});

export const createDeck = data => async dispatch => {
  dispatch(createDeckRequest());
  await OverleafAPI.init();
  return OverleafAPI.Decks.create(data)
    .then(({ deck }) =>
      dispatch(
        createDeckSuccess(normalize(deck, deckSchema, defaultDeckEntities))
      )
    )
    .catch(error => dispatch(createDeckFailure(error)));
};

const duplicateDeckRequest = () => ({
  type: types.DUPLICATE_DECK_REQUEST
});

const duplicateDeckSuccess = data => ({
  type: types.DUPLICATE_DECK_SUCCESS,
  data
});

const duplicateDeckFailure = error => ({
  type: types.DUPLICATE_DECK_FAILURE,
  error
});

export const duplicateDeck = deckId => async dispatch => {
  dispatch(duplicateDeckRequest());
  await OverleafAPI.init();
  return OverleafAPI.Decks.duplicate(deckId)
    .then(({ deck }) =>
      dispatch(
        duplicateDeckSuccess(normalize(deck, deckSchema, defaultDeckEntities))
      )
    )
    .catch(error => dispatch(duplicateDeckFailure(error)));
};

const fetchCardRequest = cardId => ({
  type: types.FETCH_CARD_REQUEST,
  cardId
});

const fetchCardSuccess = (cardId, data) => ({
  type: types.FETCH_CARD_SUCCESS,
  cardId,
  data
});

const fetchCardFailure = cardId => ({
  type: types.FETCH_CARD_FAILURE,
  cardId
});

export const fetchCard = (deckId, cardId) => async dispatch => {
  dispatch(fetchCardRequest(cardId));
  await OverleafAPI.init();
  return OverleafAPI.Cards.one(deckId, cardId)
    .then(({ card }) =>
      dispatch(
        fetchCardSuccess(
          cardId,
          normalize(card, cardSchema, defaultCardEntities)
        )
      )
    )
    .catch(error => dispatch(fetchCardFailure(cardId, error)));
};

const updateCardRequest = (cardId, data) => ({
  type: types.UPDATE_CARD_REQUEST,
  cardId,
  data
});

const updateCardSuccess = (cardId, data) => ({
  type: types.UPDATE_CARD_SUCCESS,
  cardId,
  data
});

const updateCardFailure = (cardId, oldData, error) => ({
  type: types.UPDATE_CARD_FAILURE,
  cardId,
  data: oldData, // TODO is this normalized?
  error
});

export const updateCard = (card, data) => async dispatch => {
  const cardId = card.uuid;
  dispatch(updateCardRequest(cardId, data));
  await OverleafAPI.init();
  return OverleafAPI.Cards.update(card.deckUUID, cardId, data)
    .then(({ card }) =>
      dispatch(
        updateCardSuccess(
          cardId,
          normalize(card, cardSchema, defaultCardEntities)
        )
      )
    )
    .catch(error => dispatch(updateCardFailure(cardId, card, error)));
};

const deleteCardRequest = cardId => ({
  type: types.DELETE_CARD_REQUEST,
  cardId
});

const deleteCardSuccess = card => ({
  type: types.DELETE_CARD_SUCCESS,
  card
});

const deleteCardFailure = (card, error) => ({
  type: types.DELETE_CARD_FAILURE,
  card,
  error
});

export const deleteCard = card => async dispatch => {
  const cardId = card.uuid;
  dispatch(deleteCardRequest(cardId));
  await OverleafAPI.init();
  return OverleafAPI.Cards.delete(card.deckUUID, cardId)
    .then(() => dispatch(deleteCardSuccess(card)))
    .catch(error => dispatch(deleteCardFailure(card, error)));
};

const createCardRequest = deckId => ({
  type: types.CREATE_CARD_REQUEST,
  deckId
});

const createCardSuccess = data => ({
  type: types.CREATE_CARD_SUCCESS,
  data
});

const createCardFailure = error => ({
  type: types.CREATE_CARD_FAILURE,
  error
});

export const createCard = (deckId, data) => async dispatch => {
  dispatch(createCardRequest(deckId));
  await OverleafAPI.init();
  return OverleafAPI.Cards.create(deckId, data)
    .then(({ card }) =>
      dispatch(
        createCardSuccess(normalize(card, cardSchema, defaultCardEntities))
      )
    )
    .catch(error => dispatch(createCardFailure(error)));
};

const createProficiencyRequest = (deckId, cardId) => ({
  type: types.CREATE_PROFICIENCY_REQUEST,
  deckId,
  cardId
});

const createProficiencySuccess = (deckId, cardId, proficiency) => ({
  type: types.CREATE_PROFICIENCY_SUCCESS,
  deckId,
  cardId,
  proficiency
});

const createProficiencyFailure = error => ({
  type: types.CREATE_PROFICIENCY_FAILURE,
  error
});

export const createProficiency = (deckId, cardId, level) => async dispatch => {
  dispatch(createProficiencyRequest(deckId, cardId));
  await OverleafAPI.init();
  return Overleaf.Proficiencies.create(deckId, cardId, { level })
    .then(({ proficiency }) =>
      dispatch(createProficiencySuccess(deckId, cardId, proficiency))
    )
    .catch(error => dispatch(createProficiencyFailure(error)));
};

// TODO
// We'll want to not re-fetch decks/cards if they're already loaded or currently loading

export const internal = {
  fetchDeckRequest,
  fetchDeckSuccess,
  fetchDeckFailure,
  fetchDecksRequest,
  fetchDecksSuccess,
  fetchDecksFailure,
  updateDeckRequest,
  updateDeckSuccess,
  updateDeckFailure,
  deleteDeckRequest,
  deleteDeckSuccess,
  deleteDeckFailure,
  duplicateDeckRequest,
  duplicateDeckSuccess,
  duplicateDeckFailure,
  createDeckRequest,
  createDeckSuccess,
  createDeckFailure,
  fetchCardRequest,
  fetchCardSuccess,
  fetchCardFailure,
  deleteCardRequest,
  deleteCardSuccess,
  deleteCardFailure,
  updateCardRequest,
  updateCardSuccess,
  updateCardFailure,
  createCardRequest,
  createCardSuccess,
  createCardFailure,
  createProficiencyRequest,
  createProficiencySuccess,
  createProficiencyFailure
};
